import { dateQueryString } from "@/Utils/utils";
import { PatientRegistrationFileField } from "@/components/Patient/PatientRegistrationFileField";
import RadioInput from "@/components/ui/RadioInput";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Autocomplete from "@/components/ui/autocomplete";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  CoverageTypeRecord,
  GovernmentSchemeRecord,
} from "@/types/emr/patient/patientMasterData";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  InsuranceFormValues,
  insuranceSchema,
} from "@/components/Patient/Insurance/insuranceSchema";

type InsuranceCardProps = {
  coverageTypes: CoverageTypeRecord[];
  governmentSchemes: GovernmentSchemeRecord[];
  patientId?: string;
  initialValues?: InsuranceFormValues;
  onAdd: (values: InsuranceFormValues) => Promise<boolean>;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
};

const InsuranceCard = ({
  coverageTypes,
  governmentSchemes,
  patientId,
  initialValues,
  onAdd,
  onCancel,
  onDirtyChange,
}: InsuranceCardProps) => {
  const { t } = useTranslation();

  const form = useForm<InsuranceFormValues>({
    resolver: zodResolver(insuranceSchema),
    defaultValues: initialValues ?? {
      coverage_type: undefined,
      government_scheme: undefined,
      government_scheme_other: "",
      insurance_provider_name: "",
      tpa_name: "",
      policy_or_card_number: "",
      policy_validity_date: undefined,
      pre_authorization_required: undefined,
      insurance_card_letter_file: undefined,
      insurance_card_letter_pending: undefined,
    },
  });

  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const [isFileDirty, setIsFileDirty] = useState(false);

  useEffect(() => {
    if (initialValues) {
      form.reset(initialValues);
    } else {
      form.reset({
        coverage_type: undefined,
        government_scheme: undefined,
        government_scheme_other: "",
        insurance_provider_name: "",
        tpa_name: "",
        policy_or_card_number: "",
        policy_validity_date: undefined,
        pre_authorization_required: undefined,
        insurance_card_letter_file: undefined,
        insurance_card_letter_pending: undefined,
      });
    }
  }, [initialValues, form]);

  const selectedCoverageType = coverageTypes.find(
    (item) => item.id === form.watch("coverage_type"),
  );

  const isGovernmentScheme = selectedCoverageType?.code === "government_scheme";

  const isPrivateInsurance = selectedCoverageType?.code === "private_insurance";

  const isCorporate = selectedCoverageType?.code === "corporate";

  const isTPA = selectedCoverageType?.code === "tpa";

  const selectedGovernmentScheme = governmentSchemes.find(
    (item) => item.id === form.watch("government_scheme"),
  );

  const showGovernmentSchemeOther = selectedGovernmentScheme?.is_other;

  const hasUnsavedChanges = () => {
    const values = form.getValues();

    return Object.values(values).some((value) => {
      if (value === null || value === undefined) {
        return false;
      }

      if (typeof value === "string") {
        return value.trim() !== "";
      }

      return true;
    });
  };

  const watchedValues = form.watch();

  useEffect(() => {
    const dirty = initialValues
      ? form.formState.isDirty || isFileDirty
      : hasUnsavedChanges();

    onDirtyChange?.(dirty);
  }, [watchedValues, isFileDirty, initialValues, form.formState.isDirty]);

  const onSubmit = async (values: InsuranceFormValues) => {
    const coverageCode = selectedCoverageType?.code;

    let hasError = false;

    if (coverageCode === "government_scheme") {
      if (!values.government_scheme) {
        form.setError("government_scheme", {
          type: "manual",
          message: t("government_scheme_required"),
        });
        hasError = true;
      }

      if (
        showGovernmentSchemeOther &&
        !values.government_scheme_other?.trim()
      ) {
        form.setError("government_scheme_other", {
          type: "manual",
          message: t("government_scheme_other_required"),
        });
        hasError = true;
      }
    }

    if (
      coverageCode === "private_insurance" ||
      coverageCode === "corporate" ||
      coverageCode === "tpa"
    ) {
      if (!values.insurance_provider_name?.trim()) {
        form.setError("insurance_provider_name", {
          type: "manual",
          message: t("insurance_provider_name_required"),
        });
        hasError = true;
      }
    }

    if (coverageCode === "tpa") {
      if (!values.tpa_name?.trim()) {
        form.setError("tpa_name", {
          type: "manual",
          message: t("tpa_name_required"),
        });
        hasError = true;
      }
    }

    if (!values.policy_or_card_number?.trim()) {
      form.setError("policy_or_card_number", {
        type: "manual",
        message: t("policy_or_card_number_required"),
      });
      hasError = true;
    }

    if (!values.policy_validity_date) {
      form.setError("policy_validity_date", {
        type: "manual",
        message: t("policy_validity_date_required"),
      });
      hasError = true;
    }

    if (values.pre_authorization_required === undefined) {
      form.setError("pre_authorization_required", {
        type: "manual",
        message: t("pre_authorization_required_required"),
      });
      hasError = true;
    }

    if (hasError) {
      return;
    }

    const success = await onAdd(values);

    if (!success) {
      return;
    }

    setIsFileDirty(false);

    if (!initialValues) {
      form.reset({
        coverage_type: undefined,
        government_scheme: undefined,
        government_scheme_other: "",
        insurance_provider_name: "",
        tpa_name: "",
        policy_or_card_number: "",
        policy_validity_date: undefined,
        pre_authorization_required: undefined,
        insurance_card_letter_file: undefined,
        insurance_card_letter_pending: undefined,
      });

      setTimeout(() => {
        form.setFocus("coverage_type");
      }, 0);
    }
  };

  return (
    <>
      <Card className="p-6">
        <Form {...form}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="coverage_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("coverage_type")}</FormLabel>
                    <FormControl>
                      <Autocomplete
                        ref={field.ref}
                        options={coverageTypes.map((item) => ({
                          label: item.name,
                          value: item.id,
                        }))}
                        value={form.watch("coverage_type") ?? ""}
                        onChange={(value) => {
                          const isCleared = !value;

                          if (isCleared) {
                            form.reset({
                              id: form.getValues("id"),
                              coverage_type: undefined,
                              government_scheme: undefined,
                              government_scheme_other: "",
                              insurance_provider_name: "",
                              tpa_name: "",
                              policy_or_card_number: "",
                              policy_validity_date: undefined,
                              pre_authorization_required: undefined,
                              insurance_card_letter_file: undefined,
                              insurance_card_letter_pending: undefined,
                            });

                            form.clearErrors();
                            return;
                          }

                          field.onChange(value);

                          form.setValue("government_scheme", undefined);
                          form.setValue("government_scheme_other", "");
                          form.setValue("insurance_provider_name", "");
                          form.setValue("tpa_name", "");
                          form.setValue("policy_or_card_number", "");
                          form.setValue("policy_validity_date", undefined);
                          form.setValue(
                            "pre_authorization_required",
                            undefined,
                          );
                          form.setValue(
                            "insurance_card_letter_file",
                            undefined,
                          );
                          form.setValue(
                            "insurance_card_letter_pending",
                            undefined,
                          );

                          form.clearErrors();
                        }}
                        placeholder={t("select_coverage_type")}
                        inputPlaceholder={t("search")}
                        noOptionsMessage={t("no_results_found")}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              <div />
            </div>

            {isGovernmentScheme && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="government_scheme"
                  render={({ field }) => (
                    <FormItem className="h-fit">
                      <FormLabel aria-required>
                        {t("government_scheme")}
                      </FormLabel>
                      <FormControl>
                        <Autocomplete
                          ref={field.ref}
                          options={governmentSchemes.map((item) => ({
                            label: item.name,
                            value: item.id,
                          }))}
                          value={form.watch("government_scheme") ?? ""}
                          onChange={(value) => {
                            field.onChange(value || undefined);

                            const selectedScheme = governmentSchemes.find(
                              (item) => item.id === value,
                            );

                            if (!selectedScheme?.is_other) {
                              form.setValue("government_scheme_other", "");
                              form.clearErrors("government_scheme_other");
                            }

                            form.clearErrors("government_scheme");
                          }}
                          placeholder={t("select_government_scheme")}
                          inputPlaceholder={t("search")}
                          noOptionsMessage={t("no_results_found")}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                {showGovernmentSchemeOther ? (
                  <FormField
                    control={form.control}
                    name="government_scheme_other"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel aria-required>
                          {t("government_scheme_other")}
                        </FormLabel>

                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ""}
                            placeholder={t("enter_government_scheme_other")}
                            onChange={(e) => {
                              field.onChange(e);
                              form.clearErrors("government_scheme_other");
                            }}
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <div />
                )}
              </div>
            )}

            {(isPrivateInsurance || isCorporate || isTPA) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="insurance_provider_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel aria-required>
                        {t("insurance_provider_name")}
                      </FormLabel>

                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          placeholder={t("enter_insurance_provider_name")}
                          onChange={(e) => {
                            field.onChange(e);
                            form.clearErrors("insurance_provider_name");
                          }}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div />
              </div>
            )}

            {isTPA && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="tpa_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel aria-required>{t("tpa_name")}</FormLabel>

                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          placeholder={t("enter_tpa_name")}
                          onChange={(e) => {
                            field.onChange(e);
                            form.clearErrors("tpa_name");
                          }}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div />
              </div>
            )}

            {!!selectedCoverageType && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                <FormField
                  control={form.control}
                  name="policy_or_card_number"
                  render={({ field }) => (
                    <FormItem className="flex h-full flex-col">
                      <FormLabel aria-required>
                        {t("policy_or_card_number")}
                      </FormLabel>

                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          placeholder={t("enter_policy_or_card_number")}
                          onChange={(e) => {
                            const cleaned = e.target.value
                              .replace(/[^a-zA-Z0-9]/g, "")
                              .toUpperCase();
                            field.onChange(cleaned);
                            form.clearErrors("policy_or_card_number");
                          }}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="insurance_card_letter_file"
                  render={() => (
                    <FormItem className="flex h-full flex-col">
                      <PatientRegistrationFileField
                        label={t("insurance_document")}
                        patientId={patientId}
                        allowDownload={
                          !!patientId &&
                          !!form.watch("insurance_card_letter_file")?.id &&
                          !form.formState.dirtyFields.insurance_card_letter_file
                        }
                        value={form.watch("insurance_card_letter_file")}
                        pendingFile={form.watch(
                          "insurance_card_letter_pending",
                        )}
                        onChange={({ fileRecord, pendingFile }) => {
                          form.setValue(
                            "insurance_card_letter_file",
                            fileRecord ?? undefined,
                            { shouldDirty: true },
                          );

                          form.setValue(
                            "insurance_card_letter_pending",
                            pendingFile ?? null,
                            { shouldDirty: true },
                          );

                          setIsFileDirty(true);

                          form.clearErrors("insurance_card_letter_file");
                        }}
                      />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {!!selectedCoverageType && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <FormField
                  control={form.control}
                  name="policy_validity_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel aria-required>
                        {t("policy_validity_date")}
                      </FormLabel>

                      <FormControl>
                        <DatePicker
                          className="w-full"
                          date={
                            form.watch("policy_validity_date")
                              ? new Date(form.watch("policy_validity_date")!)
                              : undefined
                          }
                          onChange={(date) => {
                            field.onChange(
                              date ? dateQueryString(date) : undefined,
                            );

                            form.clearErrors("policy_validity_date");
                          }}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);

                            return date < today;
                          }}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="pre_authorization_required"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel aria-required>
                        {t("pre_authorization_required")}
                      </FormLabel>

                      <FormControl>
                        <RadioInput
                          value={
                            form.watch("pre_authorization_required") ===
                            undefined
                              ? ""
                              : form.watch("pre_authorization_required")
                                ? "true"
                                : "false"
                          }
                          onValueChange={(value) => {
                            field.onChange(value === "true");
                            form.clearErrors("pre_authorization_required");
                          }}
                          options={[
                            {
                              value: "true",
                              label: t("yes"),
                            },
                            {
                              value: "false",
                              label: t("no"),
                            },
                          ]}
                        />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                disabled={
                  initialValues
                    ? !form.formState.isDirty && !isFileDirty
                    : !form.watch("coverage_type")
                }
                onClick={() => {
                  void form.handleSubmit(onSubmit)();
                }}
              >
                {initialValues ? t("update") : t("add")}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const shouldWarn = initialValues
                    ? form.formState.isDirty || isFileDirty
                    : hasUnsavedChanges();

                  if (!shouldWarn) {
                    onCancel();
                    return;
                  }

                  setShowDiscardDialog(true);
                }}
              >
                {t("cancel")}
              </Button>
            </div>
          </div>
        </Form>
      </Card>

      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("discard_unsaved_insurance")}
            </AlertDialogTitle>

            <AlertDialogDescription>
              {t("discard_unsaved_insurance_message")}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>

            <AlertDialogAction
              onClick={() => {
                setShowDiscardDialog(false);
                setIsFileDirty(false);
                onCancel();
              }}
            >
              {t("discard")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default InsuranceCard;
